import PropTypes from "prop-types";
import React, { useState } from "react";
import { connect } from "react-redux";
import SecondaryButton from "web/react/components/buttons/secondary-button/secondary-button";
import LoadingSpinner from "web/react/components/loading-spinner/loading-spinner";
import withReduxProvider from "web/react/redux-provider";
import { hideSeoContent } from "web/redux/ducks/feed-seo-content";
import {
    setButtonNextUrl,
    setRemainingProductCount,
    setVisibleProductCount,
} from "web/redux/ducks/feed-show-more";
import store from "web/redux/store";
import analytics from "web/script/analytics/analytics";
import { productFeedLoadMoreContent } from "web/script/components/product-feed";
import { gettext } from "web/script/modules/django-i18n";
import environment from "web/script/modules/environment";
import userProfiler from "web/script/modules/userprofiler";
import { numberFormat } from "web/script/modules/formats";
import browser from "web/script/utils/browser";
import { RawBranchLink } from "web/script/utils/branch-io";
import url from "web/script/utils/url";
import { useOverlayWatcher } from "web/react/hooks/use-overlay-watcher/use-overlay-watcher";
import history from "web/script/utils/history";
import SendToAppModal from "web/react/components/send-to-app-modal/send-to-app-modal";
import styles from "./feeds-show-more.css";

const SOFT_BLOCK = "soft_block";
const HARD_BLOCK = "hard_block";
const TITLE_1 = "title_1";
const TITLE_2 = "title_2";
const TITLE_3 = "title_3";
const TITLE_4 = "title_4";
const APP_MODAL_NAME = "send-to-app-on-feed-show-more-modal";

function mapStateToProps(state): object {
    const feedstate = state.feedShowMoreReducer;
    return {
        visibleProductCount: feedstate.visible_product_count,
        totalProductCount: feedstate.total_product_count,
        remainingProductCount: feedstate.remaining_product_count,
        buttonNextUrl: feedstate.show_more_next_url,
        buttonText: feedstate.show_more_text,
    };
}

function mapDispatchToProps(dispatch): object {
    return {
        setButtonNextUrl: (url: string): void => {
            dispatch(setButtonNextUrl(url));
        },
        setVisibleProductCount: (count: number): void => {
            dispatch(setVisibleProductCount(count));
        },
        setRemainingProductCount: (count: number): void => {
            dispatch(setRemainingProductCount(count));
        },
    };
}

function ProgressBar({ currentNumber, maximumNumber }): JSX.Element {
    const progress = currentNumber / maximumNumber;
    return (
        <div className={styles.progressBar} role="presentation">
            <span
                className={styles.inner}
                style={{
                    width: progress * 100 + "%",
                }}
            />
        </div>
    );
}

export function FeedsShowMore({
    visibleProductCount,
    totalProductCount,
    remainingProductCount,
    buttonNextUrl,
    buttonText,
    setButtonNextUrl,
    setVisibleProductCount,
    setRemainingProductCount,
}): JSX.Element | null {
    const [isLoading, setIsLoading] = useState(false);
    const PRODUCTS_PER_PAGE = environment.get("paginationProductsPerPage");

    const isIos = browser.ios;
    const blockVariant = environment.getFeature("mg_block_show_more");

    let [blocked, setBlocked] = useState(true);
    const blockModalIsOpen = !!useOverlayWatcher(APP_MODAL_NAME);

    const titleVariant = environment.getFeature("mg_block_show_more_title");
    let blockingTitle = "";

    if (titleVariant == TITLE_1) {
        // "Get personalized results from your favorite brands"
        blockingTitle = gettext("feed.show_more.app_overlay_block.title_1");
    } else if (titleVariant == TITLE_2) {
        // "Keep track of your favorite items all in one place"
        blockingTitle = gettext("feed.show_more.app_overlay_block.title_2");
    } else if (titleVariant == TITLE_3) {
        // "Shop the world’s biggest and best selection of fashion"
        blockingTitle = gettext("feed.show_more.app_overlay_block.title_3");
    } else if (titleVariant == TITLE_4) {
        // "The hottest and newest products from over 12,000 brands"
        blockingTitle = gettext("feed.show_more.app_overlay_block.title_4");
    }

    let page = 1;
    let remainingProducts = remainingProductCount ? remainingProductCount : totalProductCount;

    // feed was reset
    if (visibleProductCount <= PRODUCTS_PER_PAGE) {
        if (buttonNextUrl) {
            let parsedUrl = url.parse(buttonNextUrl);
            let parsedUrlPage = parsedUrl.searchParams.get("page");
            page = parsedUrlPage !== null ? parseInt(parsedUrlPage, 10) - 1 : page;
            remainingProducts = totalProductCount - (page - 1) * PRODUCTS_PER_PAGE;
        } else {
            remainingProducts = visibleProductCount;
        }
    }

    let messageText = "";
    if (totalProductCount > 0 || visibleProductCount == 0) {
        // "Showing {current_product_count} of {total_product_count}"
        messageText = gettext("feed_pagination.summary_showing_products", {
            current_product_count: numberFormat(visibleProductCount),
            total_product_count: numberFormat(remainingProducts),
        });
    }

    function onBlockModalClose(): void {
        history.popOverlay(APP_MODAL_NAME);
    }

    function onBlockModalOpen(): void {
        // I check the current overlay state instead of modalIsOpen
        // to get around the delay introduced by hooks
        if (history.getCurrentState().overlay != APP_MODAL_NAME) {
            history.pushOverlay(APP_MODAL_NAME);
        }
    }

    function onClickButton(): void {
        analytics.event("feeds_show_more", "click_show_more_button", "click");
        if (isIos && !userProfiler.isLoggedIn()) {
            if (blockVariant == HARD_BLOCK) {
                onBlockModalOpen();
                return;
            } else if (blockVariant == SOFT_BLOCK && blocked) {
                setBlocked(false);
                onBlockModalOpen();
                return;
            }
        }
        setIsLoading(true);
        const loadMorePromise = productFeedLoadMoreContent(buttonNextUrl);
        loadMorePromise.then(function(newButtonNextUrl) {
            setButtonNextUrl(newButtonNextUrl);
            setVisibleProductCount(
                Math.min(visibleProductCount + PRODUCTS_PER_PAGE, remainingProducts)
            );
            setRemainingProductCount(remainingProducts);
            (document.activeElement as HTMLElement).blur();
            setIsLoading(false);
            store.dispatch(hideSeoContent());
        });
    }

    let loadingHtml: JSX.Element | null = null;
    if (isLoading) {
        loadingHtml = (
            <div className={styles.loadingWrapper}>
                <LoadingSpinner />
            </div>
        );
    }

    let buttonHtml: JSX.Element | null = null;
    if (buttonNextUrl) {
        buttonHtml = <SecondaryButton onClick={onClickButton}>{buttonText}</SecondaryButton>;
    }

    return (
        <div className={styles.wrapper}>
            <div className={styles.loadingWrapper}>{loadingHtml}</div>

            <div className={styles.progressMessage}>
                <p>{messageText}</p>
            </div>

            <ProgressBar currentNumber={visibleProductCount} maximumNumber={remainingProducts} />

            <div className={styles.showMoreButton}>{buttonHtml}</div>
            <SendToAppModal
                title={blockingTitle}
                url={RawBranchLink.ShowMore}
                isOpen={blockModalIsOpen}
                onClose={onBlockModalClose}
                analyticsEventLabel={"feeds_show_more"}
            />
        </div>
    );
}

FeedsShowMore.propTypes = {
    visibleProductCount: PropTypes.number,
    totalProductCount: PropTypes.number,
    remainingProductCount: PropTypes.number,
    buttonNextUrl: PropTypes.string,
    buttonText: PropTypes.string,
    setButtonNextUrl: PropTypes.func,
    setVisibleProductCount: PropTypes.func,
    setRemainingProductCount: PropTypes.func,
};

ProgressBar.propTypes = {
    currentNumber: PropTypes.number,
    maximumNumber: PropTypes.number,
};

ProgressBar.defaultProps = {
    currentNumber: 0,
    maximumNumber: 1,
};

export default withReduxProvider(connect(mapStateToProps, mapDispatchToProps)(FeedsShowMore));
